import React from 'react';

/**
 * LoadingSpinner Component
 * Award-Winning Design System
 * 
 * Sizes: sm (16px), md (24px), lg (32px)
 */

const LoadingSpinner = ({ 
  size = 'md',
  text,
  fullScreen = false,
  className = '' 
}) => {
  const sizeClass = size === 'sm' ? 'loading-spinner-sm' : size === 'lg' ? 'loading-spinner-lg' : '';

  if (fullScreen) {
    return (
      <div className="loading-container" style={{ minHeight: '100vh' }}>
        <div className={`loading-spinner ${sizeClass}`}></div>
        {text && <span className="loading-text">{text}</span>}
      </div>
    );
  }

  if (text) {
    return (
      <div className={`loading-container ${className}`}>
        <div className={`loading-spinner ${sizeClass}`}></div>
        <span className="loading-text">{text}</span>
      </div>
    );
  }

  return <div className={`loading-spinner ${sizeClass} ${className}`}></div>;
};

/**
 * Skeleton Loader Component
 * For content placeholders
 */
export const Skeleton = ({ 
  type = 'text', // text, heading, button, avatar, card
  width,
  height,
  className = '',
  count = 1
}) => {
  const getSkeletonClass = () => {
    switch (type) {
      case 'heading':
        return 'skeleton skeleton-heading';
      case 'button':
        return 'skeleton skeleton-button';
      case 'avatar':
        return 'skeleton skeleton-avatar';
      case 'card':
        return 'skeleton';
      default:
        return 'skeleton skeleton-text';
    }
  };

  const skeletonStyle = {
    ...(width && { width }),
    ...(height && { height })
  };

  if (count > 1) {
    return (
      <>
        {Array.from({ length: count }).map((_, index) => (
          <div 
            key={index}
            className={`${getSkeletonClass()} ${className}`}
            style={skeletonStyle}
          />
        ))}
      </>
    );
  }

  return (
    <div 
      className={`${getSkeletonClass()} ${className}`}
      style={skeletonStyle}
    />
  );
};

/**
 * Card Skeleton Loader
 * Pre-built skeleton for card layouts
 */
export const CardSkeleton = () => {
  return (
    <div className="card">
      <div className="card-header">
        <Skeleton type="heading" width="60%" />
        <Skeleton type="text" width="40%" />
      </div>
      <div className="card-body">
        <Skeleton type="text" count={3} />
      </div>
    </div>
  );
};

/**
 * Table Skeleton Loader
 * Pre-built skeleton for table layouts
 */
export const TableSkeleton = ({ rows = 5, columns = 4 }) => {
  return (
    <div className="table-container">
      <table className="table">
        <thead className="table-header">
          <tr className="table-header-row">
            {Array.from({ length: columns }).map((_, index) => (
              <th key={index} className="table-header-cell">
                <Skeleton type="text" width="80%" />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }).map((_, rowIndex) => (
            <tr key={rowIndex} className="table-body-row">
              {Array.from({ length: columns }).map((_, colIndex) => (
                <td key={colIndex} className="table-cell">
                  <Skeleton type="text" width="70%" />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/**
 * List Skeleton Loader
 * Pre-built skeleton for list layouts
 */
export const ListSkeleton = ({ items = 5 }) => {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
      {Array.from({ length: items }).map((_, index) => (
        <div key={index} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)' }}>
          <Skeleton type="avatar" />
          <div style={{ flex: 1 }}>
            <Skeleton type="text" width="40%" />
            <Skeleton type="text" width="60%" style={{ marginTop: 'var(--space-xs)' }} />
          </div>
        </div>
      ))}
    </div>
  );
};

export default LoadingSpinner;